import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './components/Login';
import RegisterPage from './pages/Register';
import AcceptInvitationPage from './pages/AcceptInvitation';
import Dashboard from './pages/Dashboard';
import Clients from './pages/CustomerModule/Clients';
import Projects from './pages/ProjectModule/Projects';
import ProjectDetail from './pages/ProjectModule/ProjectDetail';
import ProjectEdit from './pages/ProjectModule/ProjectEdit';
import ProjectNew from './pages/ProjectModule/ProjectNew';
import CashflowPage from './pages/Cashflow';
import Suppliers from './pages/Suppliers';
import PurchaseOrders from './pages/PurchaseOrders';
import PurchaseOrderNew from './pages/PurchaseOrderNew';
import PurchaseOrderEdit from './pages/PurchaseOrderEdit';
import Config from './pages/Config';
import CompanyProfile from './pages/CompanyProfile';
import UserProfile from './pages/UserProfile';
import Accounting from './pages/Accounting';
import CustomerPaymentSelection from './pages/CustomerPaymentSelection';
import CustomerPayments from './pages/CustomerPayments';
import CustomerPaymentDetail from './pages/CustomerPaymentDetail';
import CommercialHome from './pages/CommercialHome';
import PriceLists from './pages/PriceListModule/PriceLists';
import ProjectPriceDetail from './pages/PriceListModule/ProjectPriceDetail';
import FunctionalUnitsSettings from './pages/FUsModule/FunctionalUnitsSettings';
import ProjectUnitsManagement from './pages/FUsModule/ProjectUnitsManagement';
import Sales from './pages/Sales';
import SalesHistory from './pages/SalesHistory';
import TransactionHistory from './pages/TransactionHistory';
import CustomerCurrentAccount from './pages/CustomerCurrentAccount';
import CustomerAccounts from './pages/CustomerAccounts';
import UsersPage from './pages/Users';
import RolesPage from './pages/Roles';
import InvitationsPage from './pages/Invitations/index';
import AccountDeactivated from './pages/AccountDeactivated';
import Unauthorized from './pages/Unauthorized';
import NoPermissions from './pages/NoPermissions';
import LandingPage from './pages/Landing';
import BillingPage from './pages/Billing/BillingPage';
import BillingSuccess from './pages/Billing/BillingSuccess';
import BillingCancel from './pages/Billing/BillingCancel';
import ProjectMonitoring from './pages/ProjectMonitoring';
import SupplierInvoices from './pages/SupplierInvoices';
import Reports from './pages/Reports';
import { PERMISSIONS } from './constants/permissions';
import './App.css';

// Componente para rutas protegidas (solo autenticación)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="p-5 text-center">Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login?unauthorized=true" replace />;
  }

  if (user?.roleName === 'SIN_ASIGNAR' && location.pathname !== '/no-permissions') {
    return <Navigate to="/no-permissions" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Componente para rutas protegidas con verificación de permisos
const PermissionRoute: React.FC<{
  children: React.ReactNode;
  requires?: string;
  requiresAny?: string[];
}> = ({ children, requires, requiresAny }) => {
  const { isAuthenticated, loading, hasPermission, hasAnyPermission, user } = useAuth();

  if (loading) {
    return <div className="p-5 text-center">Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login?unauthorized=true" replace />;
  }

  if (user?.roleName === 'SIN_ASIGNAR') {
    return <Navigate to="/no-permissions" replace />;
  }

  const lacksRequired = requires ? !hasPermission(requires) : false;
  const lacksAnyRequired = requiresAny && requiresAny.length > 0 ? !hasAnyPermission(...requiresAny) : false;

  if (lacksRequired || lacksAnyRequired) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Componente para rutas exclusivas del owner
const OwnerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, isOwner, user } = useAuth();
  const location = useLocation();

  if (loading) return <div className="p-5 text-center">Cargando...</div>;
  if (!isAuthenticated) return <Navigate to="/login?unauthorized=true" replace />;
  if (user?.roleName === 'SIN_ASIGNAR' && location.pathname !== '/no-permissions') return <Navigate to="/no-permissions" replace />;
  if (!isOwner()) return <Navigate to="/unauthorized" replace />;

  return <Layout>{children}</Layout>;
};

// Componente para rutas públicas
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="p-5 text-center">Cargando...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />

        <Route path="/register" element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } />

        <Route path="/accept-invite" element={
          <PublicRoute>
            <AcceptInvitationPage />
          </PublicRoute>
        } />

        {/* Cuenta desactivada — accesible sin autenticación */}
        <Route path="/account-deactivated" element={<AccountDeactivated />} />

        {/* Acceso Denegado (403) */}
        <Route path="/unauthorized" element={
          <ProtectedRoute>
            <Unauthorized />
          </ProtectedRoute>
        } />

        <Route path="/no-permissions" element={
          <ProtectedRoute>
            <NoPermissions />
          </ProtectedRoute>
        } />

        {/* Dashboard — accesible para todos; muestra bienvenida si no hay permisos */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />

        {/* Control de proyectos — admin */}
        <Route path="/project-monitoring" element={
          <PermissionRoute requires={PERMISSIONS.PROYECTOS.VER}>
            <ProjectMonitoring />
          </PermissionRoute>
        } />

        {/* Proyectos — requiere permiso proyectos.ver */}
        <Route path="/projects" element={
          <PermissionRoute requires={PERMISSIONS.PROYECTOS.VER}>
            <Projects />
          </PermissionRoute>
        } />

        <Route path="/projects/:id" element={
          <PermissionRoute requires={PERMISSIONS.PROYECTOS.VER}>
            <ProjectDetail />
          </PermissionRoute>
        } />

        <Route path="/projects/:id/edit" element={
          <PermissionRoute requires={PERMISSIONS.PROYECTOS.EDITAR}>
            <ProjectEdit />
          </PermissionRoute>
        } />

        <Route path="/projects/new" element={
          <PermissionRoute requires={PERMISSIONS.PROYECTOS.CREAR}>
            <ProjectNew />
          </PermissionRoute>
        } />

        <Route path="/functional-units" element={
          <PermissionRoute requiresAny={[PERMISSIONS.UNIDADES.VER, PERMISSIONS.UNIDADES.EDITAR]}>
            <FunctionalUnitsSettings />
          </PermissionRoute>
        } />

        <Route path="/functional-units/project/:projectId" element={
          <PermissionRoute requiresAny={[PERMISSIONS.UNIDADES.VER, PERMISSIONS.UNIDADES.EDITAR]}>
            <ProjectUnitsManagement />
          </PermissionRoute>
        } />

        {/* Comercial — requiere permisos del módulo comercial */}
        <Route path="/commercial" element={
          <PermissionRoute
            requiresAny={[
              PERMISSIONS.COMERCIAL.CLIENTES_VER,
              PERMISSIONS.COMERCIAL.PRECIOS_VER,
              PERMISSIONS.COMERCIAL.VENTAS_VER,
              PERMISSIONS.COMERCIAL.VENTAS_CREAR,
            ]}
          >
            <CommercialHome />
          </PermissionRoute>
        } />

        <Route path="/clients" element={
          <PermissionRoute requires={PERMISSIONS.COMERCIAL.CLIENTES_VER}>
            <Clients />
          </PermissionRoute>
        } />

        <Route path="/clients/:customerId/current-account" element={
          <PermissionRoute requires={PERMISSIONS.CONTABILIDAD.CUENTAS_CORRIENTES_VER}>
            <CustomerCurrentAccount />
          </PermissionRoute>
        } />

        <Route path="/pricelists" element={
          <PermissionRoute requires={PERMISSIONS.COMERCIAL.PRECIOS_VER}>
            <PriceLists />
          </PermissionRoute>
        } />

        <Route path="/pricelists/:projectId" element={
          <PermissionRoute requires={PERMISSIONS.COMERCIAL.PRECIOS_VER}>
            <ProjectPriceDetail />
          </PermissionRoute>
        } />

        <Route path="/sales" element={
          <PermissionRoute requires={PERMISSIONS.COMERCIAL.VENTAS_CREAR}>
            <Sales />
          </PermissionRoute>
        } />

        <Route path="/sales-history" element={
          <PermissionRoute requires={PERMISSIONS.COMERCIAL.VENTAS_VER}>
            <SalesHistory />
          </PermissionRoute>
        } />

        {/* Compras — requiere permisos del módulo compras */}
        <Route path="/suppliers" element={
          <PermissionRoute requires={PERMISSIONS.COMPRAS.PROVEEDORES_VER}>
            <Suppliers />
          </PermissionRoute>
        } />

        <Route path="/purchase-orders" element={
          <PermissionRoute requires={PERMISSIONS.COMPRAS.OC_VER}>
            <PurchaseOrders />
          </PermissionRoute>
        } />

        <Route path="/purchase-orders/new" element={
          <PermissionRoute requires={PERMISSIONS.COMPRAS.OC_CREAR}>
            <PurchaseOrderNew />
          </PermissionRoute>
        } />

        <Route path="/purchase-orders/edit/:id" element={
          <PermissionRoute requires={PERMISSIONS.COMPRAS.OC_EDITAR}>
            <PurchaseOrderEdit />
          </PermissionRoute>
        } />

        <Route path="/supplier-invoices" element={
          <PermissionRoute requires={PERMISSIONS.COMPRAS.FACTURAS_VER}>
            <SupplierInvoices />
          </PermissionRoute>
        } />

        {/* Contabilidad y Finanzas */}
        <Route path="/reports" element={
          <PermissionRoute requires={PERMISSIONS.CONTABILIDAD.REPORTES_VER}>
            <Reports />
          </PermissionRoute>
        } />

        <Route path="/customer-accounts" element={
          <PermissionRoute requires={PERMISSIONS.CONTABILIDAD.CUENTAS_CORRIENTES_VER}>
            <CustomerAccounts />
          </PermissionRoute>
        } />

        <Route path="/customer-payments" element={
          <PermissionRoute requires={PERMISSIONS.CONTABILIDAD.COBRANZAS_VER}>
            <CustomerPaymentSelection />
          </PermissionRoute>
        } />

        <Route path="/customer-payments/:customerId" element={
          <PermissionRoute requiresAny={[PERMISSIONS.CONTABILIDAD.COBRANZAS_VER, PERMISSIONS.CONTABILIDAD.COBRANZAS_OPERAR]}>
            <CustomerPayments />
          </PermissionRoute>
        } />

        <Route path="/customer-payments/:customerId/:saleId" element={
          <PermissionRoute requiresAny={[PERMISSIONS.CONTABILIDAD.COBRANZAS_VER, PERMISSIONS.CONTABILIDAD.COBRANZAS_OPERAR]}>
            <CustomerPaymentDetail />
          </PermissionRoute>
        } />

        <Route path="/accounting" element={
          <PermissionRoute requires={PERMISSIONS.CONTABILIDAD.MODULO_VER}>
            <Accounting />
          </PermissionRoute>
        } />

        <Route path="/cashflow" element={
          <PermissionRoute requires={PERMISSIONS.CONTABILIDAD.CAJAS_VER}>
            <CashflowPage />
          </PermissionRoute>
        } />

        <Route path="/transaction-history" element={
          <PermissionRoute requires={PERMISSIONS.CONTABILIDAD.TRANSACCIONES_VER}>
            <TransactionHistory />
          </PermissionRoute>
        } />

        {/* Configuración — requiere permiso config.ver */}
        <Route path="/config" element={
          <PermissionRoute requires={PERMISSIONS.CONFIGURACION.VER}>
            <Config />
          </PermissionRoute>
        } />

        <Route path="/company-profile" element={
          <PermissionRoute requires={PERMISSIONS.CONFIGURACION.VER}>
            <CompanyProfile />
          </PermissionRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        } />

        {/* Usuarios — visible para todos los usuarios autenticados */}
        <Route path="/users" element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        } />

        <Route path="/roles" element={
          <PermissionRoute requiresAny={[PERMISSIONS.USUARIOS_ROLES.ROLES_VER, PERMISSIONS.USUARIOS_ROLES.ROLES_GESTIONAR]}>
            <RolesPage />
          </PermissionRoute>
        } />

        <Route path="/invitations" element={
          <OwnerRoute>
            <InvitationsPage />
          </OwnerRoute>
        } />

        {/* Billing — solo OWNER (guarda interna en BillingPage) */}
        <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
        <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
        <Route path="/billing/cancel" element={<ProtectedRoute><BillingCancel /></ProtectedRoute>} />

        <Route path="/" element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        } />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        containerStyle={{
          top: 80,
          right: 20,
        }}
        toastOptions={{
          duration: 5000,
          style: {
            background: '#fff',
            color: '#363636',
            fontSize: '14px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            padding: '16px',
            maxWidth: '400px',
          },
          success: {
            duration: 5000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <AppContent />
    </AuthProvider>
  );
};

export default App;
